import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import PoweredByFooter from "../components/PoweredByFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { toast } from "sonner";
import { 
  ChevronLeft, Users, Search, RefreshCw, 
  Edit2, Save, X, MapPin, Briefcase, Shield,
  UserCheck, Crown
} from "lucide-react";

export default function LederOversigt({ user, selectedEvent }) {
  const navigate = useNavigate();
  const eventId = selectedEvent?.id;
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [leaderInfo, setLeaderInfo] = useState(null);
  const [volunteers, setVolunteers] = useState([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Stamdata for dropdowns
  const [mainRoles, setMainRoles] = useState([]);
  const [areas, setAreas] = useState([]);
  const [workZones, setWorkZones] = useState([]);
  
  // Redigering
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Bekræftelsesdialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false });

  const loadData = useCallback(async () => {
    if (!eventId || !token) return;
    setLoading(true);
    
    try {
      // Hent lederens frivillige
      const res = await axios.get(`${API}/events/${eventId}/leader/volunteers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeaderInfo(res.data.leader_info);
      setVolunteers(res.data.volunteers);
      setFilteredVolunteers(res.data.volunteers);
      
      // Hent stamdata
      const [mainRolesRes, areasRes, workZonesRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/main-roles`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/areas?event_id=${eventId}`, { headers: { Authorization: `Bearer ${token}` }}),
        axios.get(`${API}/work-zones?event_id=${eventId}`, { headers: { Authorization: `Bearer ${token}` }})
      ]);
      
      setMainRoles(mainRolesRes.data);
      setAreas(areasRes.data);
      setWorkZones(workZonesRes.data);
      
    } catch (error) {
      console.error("Fejl:", error);
      if (error.response?.status === 403) {
        toast.error("Du har ikke leder-rettigheder");
        navigate('/dashboard');
      } else {
        toast.error("Kunne ikke hente data");
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, token, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtrer frivillige ved søgning
  useEffect(() => {
    if (!searchTerm) {
      setFilteredVolunteers(volunteers);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredVolunteers(volunteers.filter(v => 
        v.name?.toLowerCase().includes(term) ||
        v.email?.toLowerCase().includes(term) ||
        v.phone?.toLowerCase().includes(term)
      ));
    }
  }, [searchTerm, volunteers]);

  const startEdit = (volunteer) => {
    setEditingId(volunteer.id);
    setEditData({
      main_role_id: volunteer.main_role_id || '',
      area_id: volunteer.area_id || '',
      work_zone_id: volunteer.work_zone_id || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (volunteerId) => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/events/${eventId}/leader/volunteers/${volunteerId}`,
        editData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Frivillig opdateret");
      setEditingId(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunne ikke gemme ændringer");
    } finally {
      setSaving(false);
    }
  };

  // Filtrer arbejdszoner baseret på valgt område
  const getFilteredWorkZones = (areaId) => {
    if (!areaId) return workZones;
    return workZones.filter(wz => wz.area_id === areaId);
  };

  // Find navne fra IDs
  const getMainRoleName = (id) => mainRoles.find(mr => mr.id === id)?.name || '-';
  const getAreaName = (id) => areas.find(a => a.id === id)?.name || '-';
  const getWorkZoneName = (id) => {
    const wz = workZones.find(w => w.id === id);
    return wz ? (wz.label || wz.name) : '-';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="leder-oversigt-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-500" />
                Leder-oversigt
              </h1>
              <p className="text-xs text-muted-foreground">{selectedEvent?.name}</p>
            </div>
          </div>
          
          <Button variant="ghost" size="sm" onClick={loadData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Leder-info */}
        {leaderInfo && (
          <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-yellow-500" />
              <div>
                <p className="font-bold">{leaderInfo.name}</p>
                <p className="text-sm text-muted-foreground">
                  Leder for: {getMainRoleName(leaderInfo.main_role_id) || leaderInfo.role || 'Dit hold'}
                  {leaderInfo.area_id && ` • ${getAreaName(leaderInfo.area_id)}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Søgefelt */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Søg efter navn, email eller telefon..."
              className="pl-10"
            />
          </div>
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Users className="h-4 w-4" />
            {filteredVolunteers.length} frivillige
          </div>
        </div>

        {/* Frivillige-liste */}
        {filteredVolunteers.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border rounded-lg">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchTerm ? 'Ingen frivillige matcher din søgning' : 'Ingen frivillige i dit hold endnu'}
            </p>
          </div>
        ) : (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 text-sm font-medium">Navn</th>
                  <th className="text-left p-3 text-sm font-medium">Funktion</th>
                  <th className="text-left p-3 text-sm font-medium">Område</th>
                  <th className="text-left p-3 text-sm font-medium">Arbejdszone</th>
                  <th className="text-right p-3 text-sm font-medium">Handling</th>
                </tr>
              </thead>
              <tbody>
                {filteredVolunteers.map(volunteer => (
                  <tr key={volunteer.id} className="border-t border-border hover:bg-muted/30">
                    <td className="p-3">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {volunteer.name}
                          {volunteer.is_leader && (
                            <Crown className="h-3 w-3 text-yellow-500" title="Leder" />
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">{volunteer.email}</p>
                      </div>
                    </td>
                    
                    {editingId === volunteer.id ? (
                      <>
                        {/* Redigeringstilstand */}
                        <td className="p-3">
                          <select
                            value={editData.main_role_id}
                            onChange={(e) => setEditData({ ...editData, main_role_id: e.target.value })}
                            className="w-full px-2 py-1 bg-zinc-800 border border-border rounded text-sm"
                          >
                            <option value="">Vælg...</option>
                            {mainRoles.map(mr => (
                              <option key={mr.id} value={mr.id}>{mr.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3">
                          <select
                            value={editData.area_id}
                            onChange={(e) => setEditData({ 
                              ...editData, 
                              area_id: e.target.value,
                              work_zone_id: '' // Nulstil arbejdszone når område ændres
                            })}
                            className="w-full px-2 py-1 bg-zinc-800 border border-border rounded text-sm"
                          >
                            <option value="">Vælg...</option>
                            {areas.map(a => (
                              <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3">
                          <select
                            value={editData.work_zone_id}
                            onChange={(e) => setEditData({ ...editData, work_zone_id: e.target.value })}
                            className="w-full px-2 py-1 bg-zinc-800 border border-border rounded text-sm"
                          >
                            <option value="">Vælg...</option>
                            {getFilteredWorkZones(editData.area_id).map(wz => (
                              <option key={wz.id} value={wz.id}>{wz.label || wz.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              onClick={() => saveEdit(volunteer.id)}
                              disabled={saving}
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={cancelEdit}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        {/* Visningstilstand */}
                        <td className="p-3">
                          <span className="text-sm">
                            {getMainRoleName(volunteer.main_role_id)}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-sm flex items-center gap-1">
                            {volunteer.area_id && <MapPin className="h-3 w-3 text-indigo-400" />}
                            {getAreaName(volunteer.area_id)}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-sm flex items-center gap-1">
                            {volunteer.work_zone_id && <Briefcase className="h-3 w-3 text-green-400" />}
                            {getWorkZoneName(volunteer.work_zone_id)}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(volunteer)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Hjælpetekst */}
        <div className="mt-6 p-4 bg-muted/30 border border-border rounded-lg">
          <p className="text-sm text-muted-foreground">
            💡 <strong>Tip:</strong> Som leder kan du ændre funktion, område og arbejdszone for frivillige i dit hold. 
            Hvis du skal flytte en frivillig til et helt andet hold, skal du logge ud og ind igen med det andet hold valgt.
          </p>
        </div>
        <PoweredByFooter />
      </main>

      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false })}
      />
    </div>
  );
}
